"use client";

const JSON_ZIP_MIME_TYPE = "application/zip";
const ZIP_METHOD_STORE = 0;
const ZIP_METHOD_DEFLATE = 8;
const ZIP_UTF8_FLAG = 0x0800;
const ZIP_LOCAL_FILE_HEADER_SIGNATURE = 0x04034b50;
const ZIP_CENTRAL_DIRECTORY_SIGNATURE = 0x02014b50;
const ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50;
const MAX_UINT32 = 0xffffffff;

function readUint16LE(bytes: Uint8Array, offset: number): number {
  return bytes[offset] | (bytes[offset + 1] << 8);
}

function readUint32LE(bytes: Uint8Array, offset: number): number {
  return (
    (bytes[offset] |
      (bytes[offset + 1] << 8) |
      (bytes[offset + 2] << 16) |
      (bytes[offset + 3] << 24)) >>>
    0
  );
}

function writeUint16LE(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = value & 0xff;
  bytes[offset + 1] = (value >>> 8) & 0xff;
}

function writeUint32LE(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = value & 0xff;
  bytes[offset + 1] = (value >>> 8) & 0xff;
  bytes[offset + 2] = (value >>> 16) & 0xff;
  bytes[offset + 3] = (value >>> 24) & 0xff;
}

let crcTable: Uint32Array | null = null;

function buildCrcTable(): Uint32Array {
  const table = new Uint32Array(256);
  for (let index = 0; index < table.length; index += 1) {
    let crc = index;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
    }
    table[index] = crc >>> 0;
  }
  return table;
}

function crc32(bytes: Uint8Array): number {
  const table = crcTable ?? buildCrcTable();
  crcTable = table;
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc = table[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function concatBytes(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((size, chunk) => size + chunk.byteLength, 0);
  const combined = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    combined.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return combined;
}

function arrayBufferFromBytes(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function dosDateTime(date: Date): { dosDate: number; dosTime: number } {
  const year = Math.max(1980, date.getFullYear());
  return {
    dosDate:
      date.getDate() | ((date.getMonth() + 1) << 5) | ((year - 1980) << 9),
    dosTime:
      (date.getSeconds() >> 1) |
      (date.getMinutes() << 5) |
      (date.getHours() << 11),
  };
}

async function transformBytes(
  bytes: Uint8Array,
  stream: GenericTransformStream,
): Promise<Uint8Array> {
  const result = await new Response(
    new Blob([arrayBufferFromBytes(bytes)]).stream().pipeThrough(stream),
  ).arrayBuffer();
  return new Uint8Array(result);
}

async function deflateForZip(bytes: Uint8Array): Promise<{
  method: typeof ZIP_METHOD_STORE | typeof ZIP_METHOD_DEFLATE;
  bytes: Uint8Array;
}> {
  if (typeof CompressionStream === "undefined") {
    return { method: ZIP_METHOD_STORE, bytes };
  }
  try {
    return {
      method: ZIP_METHOD_DEFLATE,
      bytes: await transformBytes(bytes, new CompressionStream("deflate-raw")),
    };
  } catch {
    return { method: ZIP_METHOD_STORE, bytes };
  }
}

async function inflateZipPayload(bytes: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream === "undefined") {
    throw new Error("This browser cannot decompress compressed share bundles.");
  }
  try {
    return await transformBytes(bytes, new DecompressionStream("deflate-raw"));
  } catch {
    throw new Error("AI check share bundle zip is corrupt.");
  }
}

function assertZip32Size(value: number, label: string): void {
  if (value > MAX_UINT32) {
    throw new Error(`${label} is too large for AI check share bundle zip.`);
  }
}

export async function createSingleJsonZipBlob(
  jsonFileName: string,
  jsonText: string,
): Promise<Blob> {
  const encoder = new TextEncoder();
  const nameBytes = encoder.encode(jsonFileName);
  const jsonBytes = encoder.encode(jsonText);
  const payload = await deflateForZip(jsonBytes);
  const checksum = crc32(jsonBytes);
  const versionNeeded = payload.method === ZIP_METHOD_DEFLATE ? 20 : 10;
  const { dosDate, dosTime } = dosDateTime(new Date());

  assertZip32Size(nameBytes.byteLength, "Share bundle file name");
  assertZip32Size(jsonBytes.byteLength, "Share bundle JSON");
  assertZip32Size(payload.bytes.byteLength, "Compressed share bundle JSON");

  const localHeader = new Uint8Array(30 + nameBytes.byteLength);
  writeUint32LE(localHeader, 0, ZIP_LOCAL_FILE_HEADER_SIGNATURE);
  writeUint16LE(localHeader, 4, versionNeeded);
  writeUint16LE(localHeader, 6, ZIP_UTF8_FLAG);
  writeUint16LE(localHeader, 8, payload.method);
  writeUint16LE(localHeader, 10, dosTime);
  writeUint16LE(localHeader, 12, dosDate);
  writeUint32LE(localHeader, 14, checksum);
  writeUint32LE(localHeader, 18, payload.bytes.byteLength);
  writeUint32LE(localHeader, 22, jsonBytes.byteLength);
  writeUint16LE(localHeader, 26, nameBytes.byteLength);
  localHeader.set(nameBytes, 30);

  const centralDirectoryOffset =
    localHeader.byteLength + payload.bytes.byteLength;
  assertZip32Size(centralDirectoryOffset, "Share bundle zip");

  const centralDirectory = new Uint8Array(46 + nameBytes.byteLength);
  writeUint32LE(centralDirectory, 0, ZIP_CENTRAL_DIRECTORY_SIGNATURE);
  writeUint16LE(centralDirectory, 4, 20);
  writeUint16LE(centralDirectory, 6, versionNeeded);
  writeUint16LE(centralDirectory, 8, ZIP_UTF8_FLAG);
  writeUint16LE(centralDirectory, 10, payload.method);
  writeUint16LE(centralDirectory, 12, dosTime);
  writeUint16LE(centralDirectory, 14, dosDate);
  writeUint32LE(centralDirectory, 16, checksum);
  writeUint32LE(centralDirectory, 20, payload.bytes.byteLength);
  writeUint32LE(centralDirectory, 24, jsonBytes.byteLength);
  writeUint16LE(centralDirectory, 28, nameBytes.byteLength);
  centralDirectory.set(nameBytes, 46);

  const endOfCentralDirectory = new Uint8Array(22);
  writeUint32LE(
    endOfCentralDirectory,
    0,
    ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE,
  );
  writeUint16LE(endOfCentralDirectory, 8, 1);
  writeUint16LE(endOfCentralDirectory, 10, 1);
  writeUint32LE(endOfCentralDirectory, 12, centralDirectory.byteLength);
  writeUint32LE(endOfCentralDirectory, 16, centralDirectoryOffset);

  return new Blob(
    [
      arrayBufferFromBytes(
        concatBytes([
          localHeader,
          payload.bytes,
          centralDirectory,
          endOfCentralDirectory,
        ]),
      ),
    ],
    { type: JSON_ZIP_MIME_TYPE },
  );
}

function findZipEndOfCentralDirectory(bytes: Uint8Array): number {
  const minOffset = Math.max(0, bytes.byteLength - 22 - 0xffff);
  for (let offset = bytes.byteLength - 22; offset >= minOffset; offset -= 1) {
    if (
      readUint32LE(bytes, offset) === ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE
    ) {
      return offset;
    }
  }
  return -1;
}

export async function isZipArchiveFile(file: File): Promise<boolean> {
  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith(".zip")) return true;
  const header = new Uint8Array(await file.slice(0, 4).arrayBuffer());
  return (
    header.byteLength >= 4 &&
    header[0] === 0x50 &&
    header[1] === 0x4b &&
    (header[2] === 0x03 || header[2] === 0x05 || header[2] === 0x07) &&
    (header[3] === 0x04 || header[3] === 0x06 || header[3] === 0x08)
  );
}

export async function readSingleJsonFromZipFile(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const endOfCentralDirectoryOffset = findZipEndOfCentralDirectory(bytes);
  if (endOfCentralDirectoryOffset < 0) {
    throw new Error("AI check share bundle zip is corrupt.");
  }

  const entryCount = readUint16LE(bytes, endOfCentralDirectoryOffset + 10);
  const centralDirectoryOffset = readUint32LE(
    bytes,
    endOfCentralDirectoryOffset + 16,
  );
  if (centralDirectoryOffset >= bytes.byteLength) {
    throw new Error("AI check share bundle zip is corrupt.");
  }

  const decoder = new TextDecoder();
  let offset = centralDirectoryOffset;
  for (let index = 0; index < entryCount; index += 1) {
    if (readUint32LE(bytes, offset) !== ZIP_CENTRAL_DIRECTORY_SIGNATURE) {
      throw new Error("AI check share bundle zip is corrupt.");
    }
    const flags = readUint16LE(bytes, offset + 8);
    if (flags & 0x0001) {
      throw new Error("Encrypted share bundle zips are not supported.");
    }
    const method = readUint16LE(bytes, offset + 10);
    const expectedCrc = readUint32LE(bytes, offset + 16);
    const compressedSize = readUint32LE(bytes, offset + 20);
    const uncompressedSize = readUint32LE(bytes, offset + 24);
    const nameLength = readUint16LE(bytes, offset + 28);
    const extraLength = readUint16LE(bytes, offset + 30);
    const commentLength = readUint16LE(bytes, offset + 32);
    const localHeaderOffset = readUint32LE(bytes, offset + 42);
    const nameStart = offset + 46;
    const nameEnd = nameStart + nameLength;
    const name = decoder.decode(bytes.slice(nameStart, nameEnd));
    offset = nameEnd + extraLength + commentLength;

    if (name.endsWith("/") || !name.toLowerCase().endsWith(".json")) {
      continue;
    }
    if (
      compressedSize === MAX_UINT32 ||
      uncompressedSize === MAX_UINT32 ||
      localHeaderOffset === MAX_UINT32
    ) {
      throw new Error("Zip64 share bundles are not supported.");
    }
    if (
      localHeaderOffset + 30 > bytes.byteLength ||
      readUint32LE(bytes, localHeaderOffset) !== ZIP_LOCAL_FILE_HEADER_SIGNATURE
    ) {
      throw new Error("AI check share bundle zip is corrupt.");
    }

    const localNameLength = readUint16LE(bytes, localHeaderOffset + 26);
    const localExtraLength = readUint16LE(bytes, localHeaderOffset + 28);
    const payloadStart =
      localHeaderOffset + 30 + localNameLength + localExtraLength;
    const payloadEnd = payloadStart + compressedSize;
    if (payloadEnd > bytes.byteLength) {
      throw new Error("AI check share bundle zip is corrupt.");
    }

    const payload = bytes.slice(payloadStart, payloadEnd);
    const jsonBytes =
      method === ZIP_METHOD_STORE
        ? payload
        : method === ZIP_METHOD_DEFLATE
          ? await inflateZipPayload(payload)
          : null;
    if (!jsonBytes) {
      throw new Error("Unsupported share bundle zip compression method.");
    }
    if (
      jsonBytes.byteLength !== uncompressedSize ||
      crc32(jsonBytes) !== expectedCrc
    ) {
      throw new Error("AI check share bundle zip is corrupt.");
    }
    return decoder.decode(jsonBytes);
  }

  throw new Error("AI check share bundle zip does not contain a JSON file.");
}
